import React from "react";
import { Box, Typography, Button } from "@mui/material";
import { LocalShipping, Lock, Payments, ArrowForward, CheckCircle } from "@mui/icons-material";
import { motion } from "framer-motion";
import { useChannelPath } from '../../../contexts/ChannelContext';

const ctaFeatures = [
  { icon: <LocalShipping />, text: "무료 방문수거", desc: "택배 기사가 직접 방문" },
  { icon: <Lock />, text: "안전한 거래", desc: "데이터 완전 삭제 인증" },
  { icon: <Payments />, text: "빠른 입금", desc: "1~2일 내 계좌 입금" },
];

const CTASection: React.FC = () => {
  const channelPath = useChannelPath();

  return (
    <Box sx={{ py: { xs: 4, md: 8 }, background: '#fff' }}>
      <Box sx={{ maxWidth: { xs: '100%', md: '50%' }, mx: 'auto', px: { xs: 2, md: 0 } }}>
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <Box
            sx={{
              borderRadius: 4,
              overflow: 'hidden',
              position: 'relative',
              background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)',
              p: { xs: 2.5, md: 6 },
              // 장식 효과
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                right: 0,
                width: '50%',
                height: '100%',
                background: 'radial-gradient(ellipse at 80% 30%, rgba(59,130,246,0.15) 0%, transparent 60%)',
                pointerEvents: 'none',
              },
              '&::after': {
                content: '""',
                position: 'absolute',
                bottom: 0,
                left: 0,
                width: '40%',
                height: '60%',
                background: 'radial-gradient(ellipse at 20% 80%, rgba(139,92,246,0.1) 0%, transparent 60%)',
                pointerEvents: 'none',
              },
            }}
          >
            <Box sx={{ position: 'relative', zIndex: 1 }}>
              {/* 상단 영역: 텍스트 + 특징 */}
              <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                alignItems: { md: 'center' },
                justifyContent: 'space-between',
                gap: { xs: 3, md: 4 },
                mb: { xs: 3, md: 4 },
              }}>
                {/* 좌측: 타이틀 */}
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{
                    fontSize: { xs: '0.7rem', md: '0.75rem' },
                    fontWeight: 700,
                    color: '#3b82f6',
                    letterSpacing: '0.1em',
                    mb: 1.5,
                    textTransform: 'uppercase',
                  }}>
                    Get Started
                  </Typography>
                  <Typography fontWeight={800} sx={{
                    fontSize: { xs: '1.3rem', md: '2rem' },
                    color: '#fff',
                    lineHeight: 1.3,
                    letterSpacing: '-0.02em',
                    mb: 1,
                  }}>
                    지금 바로<br />
                    시작하세요
                  </Typography>
                  <Typography sx={{
                    fontSize: { xs: '0.85rem', md: '0.9rem' },
                    color: '#94a3b8',
                    lineHeight: 1.6,
                  }}>
                    빠르고 안전한 매입 서비스로{' '}
                    <Box component="span" sx={{ color: '#60a5fa', fontWeight: 600 }}>
                      최고의 가격
                    </Box>
                    을 받아보세요
                  </Typography>
                </Box>

                {/* 우측: 특징 리스트 */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {ctaFeatures.map((item, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: 20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.4, delay: 0.2 + index * 0.1 }}
                      viewport={{ once: true }}
                    >
                      <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        background: 'rgba(255,255,255,0.05)',
                        border: '1px solid rgba(255,255,255,0.08)',
                        borderRadius: 2,
                        px: 2,
                        py: 1.2,
                        backdropFilter: 'blur(8px)',
                        transition: 'all 0.2s',
                        '&:hover': {
                          background: 'rgba(255,255,255,0.08)',
                          borderColor: 'rgba(59,130,246,0.3)',
                        },
                      }}>
                        <Box sx={{
                          width: 36,
                          height: 36,
                          borderRadius: '10px',
                          background: 'linear-gradient(135deg, rgba(59,130,246,0.2), rgba(139,92,246,0.2))',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#60a5fa',
                          flexShrink: 0,
                          '& svg': { fontSize: 18 },
                        }}>
                          {item.icon}
                        </Box>
                        <Box>
                          <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#e2e8f0' }}>
                            {item.text}
                          </Typography>
                          <Typography sx={{ fontSize: '0.7rem', color: '#64748b' }}>
                            {item.desc}
                          </Typography>
                        </Box>
                        <CheckCircle sx={{ fontSize: 16, color: '#34d399', ml: 'auto', opacity: 0.7 }} />
                      </Box>
                    </motion.div>
                  ))}
                </Box>
              </Box>

              {/* 구분선 */}
              <Box sx={{
                height: 1,
                background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.1), transparent)',
                mb: { xs: 3, md: 4 },
              }} />

              {/* 하단: CTA 버튼 영역 */}
              <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
              }}>
                <Typography sx={{
                  fontSize: '0.8rem',
                  color: '#64748b',
                  textAlign: { xs: 'center', sm: 'left' },
                }}>
                  회원가입 없이 바로 신청 가능합니다
                </Typography>

                <motion.div
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                >
                  <Button
                    variant="contained"
                    size="large"
                    href={channelPath('/apply')}
                    endIcon={<ArrowForward />}
                    sx={{
                      borderRadius: 50,
                      px: { xs: 4, md: 5 },
                      py: { xs: 1.4, md: 1.6 },
                      fontWeight: 700,
                      fontSize: { xs: '0.9rem', md: '0.95rem' },
                      background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                      color: '#fff',
                      boxShadow: '0 0 24px rgba(59, 130, 246, 0.4), 0 4px 12px rgba(59, 130, 246, 0.3)',
                      textTransform: 'none',
                      whiteSpace: 'nowrap',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #60a5fa 0%, #3b82f6 100%)',
                        boxShadow: '0 0 32px rgba(59, 130, 246, 0.5), 0 8px 20px rgba(59, 130, 246, 0.4)',
                      },
                      transition: 'all 0.3s ease',
                    }}
                  >
                    매입 신청하기
                  </Button>
                </motion.div>
              </Box>
            </Box>
          </Box>
        </motion.div>
      </Box>
    </Box>
  );
};

export default CTASection;
